import React, { useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Brain,
  Layout,
  Megaphone,
  Globe,
  Zap,
  Sparkles,
  Clock,
  Calendar,
  Copy,
  RefreshCw,
  Lock,
} from 'lucide-react';
import './AdvanceSettings.css'; // Keeping import to avoid build errors if referenced elsewhere, but file will be empty
import PromptGeneratorModal from './PromptGeneratorModal';
import { useUser, USER_ROLES } from '../../../../../../context/UserContext';

function AdvanceSettings({
  isEditMode,
  chatbotId,
  openAIVectorStoreID,
  setOpenAIVectorStoreID,
  openAIAssistantID,
  setOpenAIAssistantID,
  openAIAPIKey,
  setOpenAIAPIKey,
  chatbotRolePrompt,
  setChatbotRolePrompt,
  aiModelTier = 'standard',
  setAiModelTier,
  customAPIEndpoint,
  setCustomAPIEndpoint,
  responseDelay,
  setResponseDelay,
  openChatOnLoad,
  setOpenChatOnLoad,
  chatOpenDelay,
  setChatOpenDelay,
  soundNotificationEnabled,
  setSoundNotificationEnabled,
  updateConfig,
  showPoweredBy,
  setShowPoweredBy,
  widgetPosition,
  setWidgetPosition,
  announcement,
  setAnnouncement,
  excludedUrls = [],
  setExcludedUrls,
  officeHours,
  setOfficeHours,
}) {
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [availableVectorStores, setAvailableVectorStores] = useState([]);
  const [isLoadingVectorStores, setIsLoadingVectorStores] = useState(false);
  const [vectorStoreError, setVectorStoreError] = useState(null);
  const [tierLockNotice, setTierLockNotice] = useState(null);

  const { user, userRole, hasFeatureAccess } = useUser();
  const canUsePremiumModel = Boolean(
    hasFeatureAccess?.('premium_ai_model') ||
    userRole === USER_ROLES.UNLIMITED ||
    userRole === USER_ROLES.ADMIN ||
    user?.email?.toLowerCase() === 'asiffarhankhan@yahoo.com'
  );

  const fetchVectorStores = useCallback(async () => {
    if (!chatbotId) return;
    setIsLoadingVectorStores(true);
    setVectorStoreError(null);
    try {
      const res = await fetch(`/api/openai-assistant/vector-stores/${chatbotId}`);
      const data = await res.json();
      if (res.ok && data.vectorStores) {
        setAvailableVectorStores(data.vectorStores);
      } else {
        setVectorStoreError(data.error || 'Failed to fetch vector stores');
      }
    } catch (err) {
      setVectorStoreError(err.message);
    } finally {
      setIsLoadingVectorStores(false);
    }
  }, [chatbotId]);

  // persistAndUpdate now iterates over the partial config so that the parent's updateConfig
  // (which expects a key and a value) is called for each property.
  const persistAndUpdate = useCallback((partialConfig) => {
    // Only merge localStorage when not editing
    if (!isEditMode) {
      const savedConfig = JSON.parse(localStorage.getItem('chatbotConfig')) || {};
      const newConfig = { ...savedConfig, ...partialConfig };
      localStorage.setItem('chatbotConfig', JSON.stringify(newConfig));
    }
    // In all cases, update the parent config
    // updateConfig expects an object, not (key, value)
    updateConfig(partialConfig);
  }, [updateConfig, isEditMode]);

  // Office Hours Logic
  const hoursConfig = useMemo(() => {
    const defaultSchedule = {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '09:00', end: '17:00' },
      sunday: { enabled: false, start: '09:00', end: '17:00' }
    };

    const savedSchedule = officeHours?.schedule || {};

    // Merge saved schedule with defaults to ensure all days exist
    const mergedSchedule = { ...defaultSchedule };
    Object.keys(savedSchedule).forEach(day => {
      if (mergedSchedule[day]) {
        mergedSchedule[day] = { ...mergedSchedule[day], ...savedSchedule[day] };
      }
    });

    return {
      enabled: officeHours?.enabled || false,
      timezone: officeHours?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      schedule: mergedSchedule,
      offlineMessage: officeHours?.offlineMessage || "We are currently closed. Please leave a message."
    };
  }, [officeHours]);

  const handleOfficeHoursChange = useCallback((key, value) => {
    // We need to update local state AND persist
    const newConfig = { ...hoursConfig, [key]: value };
    if (setOfficeHours) setOfficeHours(newConfig);
    persistAndUpdate({ officeHours: newConfig });
  }, [hoursConfig, setOfficeHours, persistAndUpdate]);

  const handleScheduleChange = useCallback((day, key, value) => {
    const newSchedule = {
      ...hoursConfig.schedule,
      [day]: {
        ...hoursConfig.schedule[day],
        [key]: value
      }
    };
    handleOfficeHoursChange('schedule', newSchedule);
  }, [hoursConfig, handleOfficeHoursChange]);

  const copyToAllWeekdays = useCallback(() => {
    // Copy Monday's start/end to Tues-Fri
    const source = hoursConfig.schedule.monday;
    const newSchedule = { ...hoursConfig.schedule };
    ['tuesday', 'wednesday', 'thursday', 'friday'].forEach(day => {
      newSchedule[day] = {
        ...newSchedule[day],
        start: source.start,
        end: source.end,
        enabled: true
      };
    });
    handleOfficeHoursChange('schedule', newSchedule);
  }, [hoursConfig, handleOfficeHoursChange]);

  const handleShowPoweredByChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      setShowPoweredBy(checked);
      persistAndUpdate({ showPoweredBy: checked });
    },
    [persistAndUpdate, setShowPoweredBy]
  );

  const handleVectorStoreIDChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (setOpenAIVectorStoreID) setOpenAIVectorStoreID(value);
      persistAndUpdate({ openAIVectorStoreID: value });
    },
    [setOpenAIVectorStoreID, persistAndUpdate]
  );

  const handleApiKeyChange = useCallback(
    (e) => {
      const value = e.target.value;
      setOpenAIAPIKey(value);
      persistAndUpdate({ openAIAPIKey: value });
    },
    [setOpenAIAPIKey, persistAndUpdate]
  );

  const handleRolePromptChange = useCallback(
    (e) => {
      const value = e.target.value;
      setChatbotRolePrompt(value);
      persistAndUpdate({ chatbotRolePrompt: value });
    },
    [setChatbotRolePrompt, persistAndUpdate]
  );

  const handleGeneratedPrompt = useCallback((prompt) => {
    setChatbotRolePrompt(prompt);
    persistAndUpdate({ chatbotRolePrompt: prompt });
  }, [setChatbotRolePrompt, persistAndUpdate]);

  const handleCustomAPIChange = useCallback(
    (e) => {
      const value = e.target.value;
      setCustomAPIEndpoint(value);
      persistAndUpdate({ customAPIEndpoint: value });
    },
    [setCustomAPIEndpoint, persistAndUpdate]
  );

  const handleModelTierChange = useCallback(
    (tier) => {
      setAiModelTier(tier);
      persistAndUpdate({ aiModelTier: tier });
    },
    [setAiModelTier, persistAndUpdate]
  );

  const handleResponseDelayChange = useCallback(
    (e) => {
      const value = Number(e.target.value);
      setResponseDelay(value);
      persistAndUpdate({ responseDelay: value });
    },
    [setResponseDelay, persistAndUpdate]
  );

  const handleOpenChatOnLoadChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      setOpenChatOnLoad(checked);
      persistAndUpdate({ openChatOnLoad: checked });
    },
    [setOpenChatOnLoad, persistAndUpdate]
  );

  const handleChatOpenDelayChange = useCallback(
    (e) => {
      const value = Number(e.target.value);
      setChatOpenDelay(value);
      persistAndUpdate({ chatOpenDelay: value });
    },
    [setChatOpenDelay, persistAndUpdate]
  );

  const handleSoundNotificationChange = useCallback(
    (e) => {
      const checked = e.target.checked;
      setSoundNotificationEnabled(checked);
      persistAndUpdate({ soundNotificationEnabled: checked });
    },
    [setSoundNotificationEnabled, persistAndUpdate]
  );

  const handleWidgetPositionChange = useCallback(
    (pos) => {
      setWidgetPosition(pos);
      persistAndUpdate({ widgetPosition: pos });
    },
    [setWidgetPosition, persistAndUpdate]
  );

  const handleAnnouncementChange = useCallback(
    (value) => {
      const newAnnouncement = {
        ...announcement,
        text: value
      };
      setAnnouncement(newAnnouncement);
      persistAndUpdate({ announcement: newAnnouncement });
    },
    [announcement, setAnnouncement, persistAndUpdate]
  );

  const handleAnnouncementToggle = useCallback(
    (e) => {
      const newAnnouncement = {
        ...announcement,
        enabled: e.target.checked
      };
      setAnnouncement(newAnnouncement);
      persistAndUpdate({ announcement: newAnnouncement });
    },
    [announcement, setAnnouncement, persistAndUpdate]
  );

  const handleStyleChange = useCallback(
    (style) => {
      const newAnnouncement = {
        ...announcement,
        style
      };
      setAnnouncement(newAnnouncement);
      persistAndUpdate({ announcement: newAnnouncement });
    },
    [announcement, setAnnouncement, persistAndUpdate]
  );

  // Define toolbar options for the rich text editor
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ];

  const handleExcludedUrlsChange = useCallback((e) => {
    const value = e.target.value || '';
    const lines = value
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (setExcludedUrls) {
      setExcludedUrls(lines);
    }
  }, [setExcludedUrls]);

  return (
    <div className="flex h-full w-full bg-slate-50/50 p-3 gap-3 overflow-hidden">
      <PromptGeneratorModal
        isOpen={isPromptModalOpen}
        onClose={() => setIsPromptModalOpen(false)}
        onGenerate={handleGeneratedPrompt}
      />
      <div className="w-full h-full flex flex-col bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 bg-white sticky top-0 z-10">
          <h2 className="text-lg font-bold tracking-tight text-slate-900">General Settings</h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">Configure your chatbot's core behavior and appearance.</p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="max-w-3xl mx-auto px-8 py-8 space-y-10">

            {/* 1. AI & Behavior */}
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Brain size={18} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-900">AI & Behavior</h3>
                  <p className="text-xs text-slate-500">Manage how your chatbot interacts and responds.</p>
                </div>
              </div>

              <div className="pl-11 space-y-6">
                {/* Vector Store ID & OpenAI Configuration (Responses API) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-slate-700">OpenAI Vector Store ID</label>
                      {chatbotId && (
                        <button
                          type="button"
                          onClick={fetchVectorStores}
                          disabled={isLoadingVectorStores}
                          className="text-[11px] text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium transition-colors"
                        >
                          <RefreshCw size={10} className={isLoadingVectorStores ? 'animate-spin' : ''} />
                          {isLoadingVectorStores ? 'Discovering...' : 'Discover Stores'}
                        </button>
                      )}
                    </div>
                    {availableVectorStores.length > 0 ? (
                      <select
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-slate-700"
                        value={openAIVectorStoreID || ''}
                        onChange={handleVectorStoreIDChange}
                      >
                        <option value="">Select a Vector Store</option>
                        {availableVectorStores.map((vs) => (
                          <option key={vs.id} value={vs.id}>
                            {vs.name || 'Unnamed'} ({vs.id}) - {vs.file_counts?.total || 0} files
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400 font-mono text-xs"
                        placeholder="vs_..."
                        value={openAIVectorStoreID || ''}
                        onChange={handleVectorStoreIDChange}
                      />
                    )}
                    <p className="text-[10px] text-slate-500">
                      OpenAI Responses API vector storage for file search (keeps existing embeddings & files).
                    </p>
                    {vectorStoreError && (
                      <p className="text-[10px] text-red-500">{vectorStoreError}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700">OpenAI API Key</label>
                    <input
                      type="password"
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                      placeholder="Enter API Key (if custom ID used)"
                      value={openAIAPIKey || ''}
                      onChange={handleApiKeyChange}
                      disabled={!openAIVectorStoreID && !openAIAssistantID}
                    />
                    <p className="text-[10px] text-slate-400">Optional custom key; platform default used if blank.</p>
                  </div>
                </div>

                {/* Legacy Assistant ID info if present */}
                {openAIAssistantID && (
                  <div className="p-3 bg-amber-50/70 border border-amber-200/70 rounded-lg flex items-center justify-between text-xs">
                    <div>
                      <span className="font-semibold text-amber-800">Legacy Assistant ID:</span>{' '}
                      <code className="text-amber-700 bg-amber-100/60 px-1 py-0.5 rounded font-mono">{openAIAssistantID}</code>
                      <p className="text-[11px] text-amber-600 mt-0.5">
                        Migrated to Responses API with Vector Store ID. Assistants API was sunset by OpenAI.
                      </p>
                    </div>
                    {setOpenAIAssistantID && (
                      <button
                        type="button"
                        onClick={() => {
                          setOpenAIAssistantID('');
                          persistAndUpdate({ openAIAssistantID: '' });
                        }}
                        className="text-amber-700 hover:text-amber-900 text-xs font-medium px-2 py-1 rounded bg-amber-100/80 hover:bg-amber-200/80 transition-colors"
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                )}

                {/* Role Prompt */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-700">Chatbot System Prompt</label>
                    <button
                      onClick={() => setIsPromptModalOpen(true)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1 font-medium"
                    >
                      <Sparkles size={12} />
                      Auto-Generate
                    </button>
                  </div>
                  <textarea
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400 min-h-[100px] resize-y"
                    placeholder="You are a helpful Customer Support Executive..."
                    value={chatbotRolePrompt}
                    onChange={handleRolePromptChange}
                  ></textarea>
                  <p className="text-[10px] text-slate-400">Define the personality and core instructions for your AI.</p>
                </div>

                {/* Model Tier Selector */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">AI Model</label>
                  <div className="flex bg-white p-1 rounded-lg border border-slate-200 shadow-sm flex-wrap gap-1">
                    {[
                      { id: 'fastest', label: 'Fastest', sublabel: 'gpt-5-mini' },
                      { id: 'standard', label: 'Standard', sublabel: 'gpt-5' },
                      { id: 'advanced', label: 'Advanced', sublabel: 'gpt-5.2' },
                      { id: 'premium', label: 'Premium', sublabel: 'gpt-6-astra', locked: !canUsePremiumModel }
                    ].map(tier => (
                      <button
                        key={tier.id}
                        type="button"
                        onClick={() => {
                          if (tier.id === 'premium' && !canUsePremiumModel) {
                            setTierLockNotice('The Premium AI model (gpt-6-astra) is exclusively available on the Unlimited plan.');
                            return;
                          }
                          setTierLockNotice(null);
                          handleModelTierChange(tier.id);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${aiModelTier === tier.id
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                          } ${tier.locked ? 'opacity-85' : ''}`}
                      >
                        <span>{tier.label}</span>
                        <span className={`text-[10px] ${aiModelTier === tier.id ? 'text-slate-300' : 'text-slate-400'}`}>
                          ({tier.sublabel})
                        </span>
                        {tier.locked && (
                          <span className="flex items-center gap-0.5 text-[9px] bg-amber-100 text-amber-800 px-1 py-0.5 rounded font-semibold ml-0.5">
                            <Lock size={9} />
                            Unlimited
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                  {tierLockNotice ? (
                    <p className="text-[11px] text-amber-600 font-medium">{tierLockNotice}</p>
                  ) : (
                    <p className="text-[10px] text-slate-400">
                      Advanced runs on gpt-5.2. Premium runs on gpt-6-astra (available exclusively for Unlimited users).
                    </p>
                  )}
                </div>

                {/* Response Delay */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                    Response Delay
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Seconds</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-32 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all"
                    value={responseDelay}
                    onChange={handleResponseDelayChange}
                  />
                </div>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* 2. Widget Experience */}
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Layout size={18} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-900">Widget Experience</h3>
                  <p className="text-xs text-slate-500">Customize positioning and automated behaviors.</p>
                </div>
              </div>

              <div className="pl-11 space-y-6">
                {/* Widget Position */}
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <span className="text-sm font-medium text-slate-700">Widget Position</span>
                  <div className="flex bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
                    <button
                      onClick={() => handleWidgetPositionChange('left')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${widgetPosition === 'left'
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                      Left
                    </button>
                    <button
                      onClick={() => handleWidgetPositionChange('right')}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${widgetPosition === 'right'
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                      Right
                    </button>
                  </div>
                </div>

                {/* Pop up on Load */}
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="space-y-0.5">
                    <span className="text-sm font-medium text-slate-700 block">Auto-Open Chat</span>
                    <span className="text-xs text-slate-400">Open widget automatically when site loads</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {openChatOnLoad && (
                      <div className="flex items-center gap-2 mr-2">
                        <span className="text-xs text-slate-500">Delay (s):</span>
                        <input
                          type="number"
                          min="0"
                          value={chatOpenDelay}
                          onChange={handleChatOpenDelayChange}
                          className="w-16 px-2 py-1 text-xs border border-slate-200 rounded focus:border-indigo-500 outline-none"
                        />
                      </div>
                    )}
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={openChatOnLoad}
                        onChange={handleOpenChatOnLoadChange}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </div>

                {/* Sound Notification */}
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="space-y-0.5">
                    <span className="text-sm font-medium text-slate-700 block">Notification Sounds</span>
                    <span className="text-xs text-slate-400">Play sound on new messages</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={soundNotificationEnabled}
                      onChange={handleSoundNotificationChange}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>

                {/* Powered By */}
                <div className="flex items-center justify-between p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <div className="space-y-0.5">
                    <span className="text-sm font-medium text-slate-700 block">SmushLabs Branding</span>
                    <span className="text-xs text-slate-400">Show "Powered by SmushLabs" in footer</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showPoweredBy}
                      onChange={handleShowPoweredByChange}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* 3. Announcement */}
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-amber-50 text-amber-600 rounded-lg">
                  <Megaphone size={18} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-900">Announcement Bar</h3>
                  <p className="text-xs text-slate-500">Display important notices or offers in the chat.</p>
                </div>
                <div className="ml-auto">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={announcement.enabled}
                      onChange={handleAnnouncementToggle}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-500"></div>
                  </label>
                </div>
              </div>

              {announcement.enabled && (
                <div className="pl-11 space-y-4">
                  <div className="prose-editor-container border border-slate-200 rounded-lg overflow-hidden bg-white">
                    <ReactQuill
                      theme="snow"
                      value={announcement.text}
                      onChange={handleAnnouncementChange}
                      modules={modules}
                      formats={formats}
                      placeholder="Write your announcement here..."
                      className="text-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-700">Alert Style</label>
                    <div className="flex gap-2">
                      {['info', 'warning', 'success'].map(style => (
                        <button
                          key={style}
                          type="button"
                          onClick={() => handleStyleChange(style)}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md capitalize border transition-all ${announcement.style === style
                            ? style === 'info' ? 'bg-blue-50 text-blue-700 border-blue-200 ring-1 ring-blue-500' :
                              style === 'warning' ? 'bg-amber-50 text-amber-700 border-amber-200 ring-1 ring-amber-500' :
                                'bg-emerald-50 text-emerald-700 border-emerald-200 ring-1 ring-emerald-500'
                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                            }`}
                        >
                          {style}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr className="border-slate-100" />

            {/* 4. Office Hours */}
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                  <Clock size={18} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-900">Office Hours</h3>
                  <p className="text-xs text-slate-500">Set availability schedule and offline messages.</p>
                </div>
                <div className="ml-auto">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hoursConfig.enabled}
                      onChange={(e) => handleOfficeHoursChange('enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>

              {hoursConfig.enabled && (
                <div className="pl-11 space-y-6">
                  {/* Timezone & Offline Message */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                        <Globe size={12} className="text-slate-400" />
                        Timezone
                      </label>
                      <div className="relative">
                        <select
                          value={hoursConfig.timezone}
                          onChange={(e) => handleOfficeHoursChange('timezone', e.target.value)}
                          className="w-full pl-3 pr-8 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 appearance-none"
                        >
                          {Intl.supportedValuesOf('timeZone').map(tz => (
                            <option key={tz} value={tz}>{tz}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none text-slate-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-slate-700">Offline Message</label>
                      <input
                        type="text"
                        value={hoursConfig.offlineMessage}
                        onChange={(e) => handleOfficeHoursChange('offlineMessage', e.target.value)}
                        placeholder="We are currently closed. Please leave a message."
                        className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400"
                      />
                    </div>
                  </div>

                  {/* Weekly Schedule */}
                  <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-3 border-b border-slate-200 flex justify-between items-center bg-white">
                      <h4 className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                        <Calendar size={14} className="text-indigo-500" />
                        Weekly Schedule
                      </h4>
                      <button
                        type="button"
                        onClick={copyToAllWeekdays}
                        className="text-[10px] flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 font-medium bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-md transition-colors"
                      >
                        <Copy size={10} />
                        Copy Mon to Weekdays
                      </button>
                    </div>

                    <div className="p-2 space-y-1">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                        <div key={day} className="flex items-center gap-3 p-2 hover:bg-white rounded-lg transition-colors group">
                          {/* Day Toggle */}
                          <div className="flex items-center gap-3 w-28 shrink-0">
                            <input
                              type="checkbox"
                              checked={hoursConfig.schedule[day]?.enabled}
                              onChange={(e) => handleScheduleChange(day, 'enabled', e.target.checked)}
                              className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500 cursor-pointer"
                            />
                            <span className={`text-sm font-medium capitalize ${hoursConfig.schedule[day]?.enabled ? 'text-slate-700' : 'text-slate-400'}`}>
                              {day.slice(0, 3)}
                            </span>
                          </div>

                          {/* Time Inputs */}
                          {hoursConfig.schedule[day]?.enabled ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                type="time"
                                value={hoursConfig.schedule[day].start}
                                onChange={(e) => handleScheduleChange(day, 'start', e.target.value)}
                                className="px-2 py-1 text-xs bg-white border border-slate-200 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-slate-600"
                              />
                              <span className="text-slate-300 text-xs">—</span>
                              <input
                                type="time"
                                value={hoursConfig.schedule[day].end}
                                onChange={(e) => handleScheduleChange(day, 'end', e.target.value)}
                                className="px-2 py-1 text-xs bg-white border border-slate-200 rounded-md focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none text-slate-600"
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-slate-400 italic pl-1">Closed</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <hr className="border-slate-100" />

            {/* 5. Advanced & Security */}
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-1.5 bg-slate-100 text-slate-600 rounded-lg">
                  <Globe size={18} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-sm font-semibold text-slate-900">Advanced & Security</h3>
                  <p className="text-xs text-slate-500">Technical configuration and restrictions.</p>
                </div>
              </div>

              <div className="pl-11 space-y-6">
                {/* Custom API */}
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700">Custom API Endpoint</label>
                  <div className="relative">
                    <Zap size={14} className="absolute left-3 top-2.5 text-slate-400" />
                    <input
                      type="text"
                      className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400 font-mono"
                      placeholder="https://api.example.com/v1/chat"
                      value={customAPIEndpoint}
                      onChange={handleCustomAPIChange}
                    />
                  </div>
                </div>

                {/* Exclusions */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-700">Exclude URL Paths</label>
                    <span className="text-[10px] text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded border border-slate-100">Supports wildcards *</span>
                  </div>
                  <textarea
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all placeholder:text-slate-400 font-mono text-slate-600"
                    placeholder="/login/*&#10;/admin/*&#10;https://example.com/secret"
                    value={(excludedUrls || []).join('\n')}
                    onChange={handleExcludedUrlsChange}
                    rows={4}
                  ></textarea>
                  <p className="text-[10px] text-slate-400 mt-1">One URL pattern per line.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

AdvanceSettings.propTypes = {
  chatbotId: PropTypes.string,
  openAIVectorStoreID: PropTypes.string,
  setOpenAIVectorStoreID: PropTypes.func,
  openAIAssistantID: PropTypes.string,
  setOpenAIAssistantID: PropTypes.func,
  openAIAPIKey: PropTypes.string.isRequired,
  setOpenAIAPIKey: PropTypes.func.isRequired,
  chatbotRolePrompt: PropTypes.string.isRequired,
  setChatbotRolePrompt: PropTypes.func.isRequired,
  aiModelTier: PropTypes.string,
  setAiModelTier: PropTypes.func,
  customAPIEndpoint: PropTypes.string.isRequired,
  setCustomAPIEndpoint: PropTypes.func.isRequired,
  responseDelay: PropTypes.number.isRequired,
  setResponseDelay: PropTypes.func.isRequired,
  openChatOnLoad: PropTypes.bool.isRequired,
  setOpenChatOnLoad: PropTypes.func.isRequired,
  chatOpenDelay: PropTypes.number.isRequired,
  setChatOpenDelay: PropTypes.func.isRequired,
  soundNotificationEnabled: PropTypes.bool.isRequired,
  setSoundNotificationEnabled: PropTypes.func.isRequired,
  updateConfig: PropTypes.func.isRequired,
  showPoweredBy: PropTypes.bool.isRequired,
  setShowPoweredBy: PropTypes.func.isRequired,
  widgetPosition: PropTypes.string.isRequired,
  setWidgetPosition: PropTypes.func.isRequired,
  announcement: PropTypes.shape({
    text: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    style: PropTypes.oneOf(['info', 'warning', 'success']).isRequired,
  }).isRequired,
  setAnnouncement: PropTypes.func.isRequired,
  excludedUrls: PropTypes.arrayOf(PropTypes.string),
  setExcludedUrls: PropTypes.func,
  isEditMode: PropTypes.bool,
  officeHours: PropTypes.object,
  setOfficeHours: PropTypes.func,
};

export default AdvanceSettings;
